import * as $ from 'jquery';
import { disposableSubscription, Lazy, serialDisposable } from 'common/barrels/core';
import { Observable } from 'common/barrels/rx';
import { newGuid } from 'common/functions/guid';
import * as moment from 'moment';
import { Map, OrderedSet } from 'immutable';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { from, click, filteredKeys } from 'common/rxui/core';
import { and } from 'common/functions/observable';
import { registerBinding } from 'runtime/bindingengine';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';

import 'runtime/styles/controls/commentbox.less';

export class CommentBox extends Control {
    private $display: JQuery;
    private $input: JQuery;
    private $add: JQuery;
    private comments = Map<string, JQuery>();
    private items: OrderedSet<string>;
    private commentContext: string;
    private fieldBinding = serialDisposable(null);
    private viewInfo = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    constructor(args: ControlArgs) {
        super(args);

        this.$element.addClass('control-container commentbox');

        this.$display = $('<div />').addClass('commentbox-display').appendTo(this.$element);
        $('<div />').addClass('commentbox-label').text(this.resources.getCoreString('XamlAddNewComment')).appendTo(this.$element);
        this.$input = $('<input type="text" />').addClass('commentbox-input').appendTo(this.$element);
        this.$add = $('<input type="button" />').addClass('commentbox-add ui-state-default').val(this.resources.getCoreString('XamlAdd')).attr('disabled', 'true').appendTo(this.$element);

        const spellcheck = this.config['spellcheck'];
        if (spellcheck === 'true')
            this.$input.attr('spellcheck', 'true');

        if (spellcheck === 'false')
            this.$input.attr('spellcheck', 'false');

        this.methods.register(this.config.elementName, this);

        this.items = OrderedSet<string>();

        this.applyTooltip(this.$element);

        this.subscriptions = disposableSubscription(
            from(this.$input, 'input propertychange')
                .map(() => this.$input.val() === '')
                .subscribe(x => (x ? this.$add.attr('disabled', 'true') : this.$add.removeAttr('disabled'))),

            click(this.$add).map(() => true)
                .merge(filteredKeys(this.$input, [13]).map(() => true))
                .map(() => this.$input.val())
                .filter(x => x !== '')
                .subscribe(x => this.addComment(x)),

            and(this.container.activatedStream, this.visibilityStream).distinctUntilChanged().filter(x => x)
                .withLatestFrom(Observable.fromEvent(this.$display, 'scroll').map(() => this.$display.scrollTop()), (x, y) => y)
                .subscribe(x => this.$display.scrollTop(x)),

            this.processBindings(this.config),
            this.managePrimaryBinding('content.CommentField')
        );

        this.initialize();
    }

    dispose() {
        super.dispose();
        this.fieldBinding.dispose();
    }

    private addComment(text: string) {
        var newCommentId = newGuid();

        this.values.update('Field', newCommentId, 'CommentUserDisplayName', this.viewInfo.value().currentUserDisplayName);
        this.values.update('Field', newCommentId, 'CommentUserId', this.viewInfo.value().currentUserId);
        this.values.update('Field', newCommentId, 'CommentDate', moment.utc().toISOString());
        this.values.update('Field', newCommentId, 'CommentText', text);
        this.values.update('Field', newCommentId, 'WorkItemId', this.viewInfo.value().workItemId);

        this.values.update('Field', this.commentContext, 'CommentItems', this.items.add(newCommentId).toArray());

        this.$input.val('');
    }

    'content.CommentField'(value) {
        this.checkInitialValueReceived();
        this.commentContext = value;
        this.fieldBinding.set(this.values.bindOneWay('Field', value, 'CommentItems', (x, y) => this.commentItems(y, x)));
    }

    commentItems(fieldContext, value: string[]) {
        const items = OrderedSet(value);

        if (items.equals(this.items))
            return;

        const newItems = items.subtract(this.items);
        const removedItems = this.items.subtract(items);

        newItems.forEach(x => {
            var bindingDisposables = disposableSubscription(
                this.values.bindOneWay('Field', x, 'CommentUserDisplayName',(v, c) => this.commentUserDisplayName(c, v)),
                this.values.bindOneWay('Field', x, 'CommentUserId',(v, c) => this.commentUserId(c, v)),
                this.values.bindOneWay('Field', x, 'CommentDate', (v, c) => this.commentDate(c, v)),
                this.values.bindOneWay('Field', x, 'CommentText', (v, c) => this.commentText(c, v)),
                this.values.bindOneWay('Field', x, 'WorkItemId', (v, c) => this.workItemId(c, v))
            );

            this.getElement(x).on('remove', () => bindingDisposables.dispose());
        });

        removedItems.forEach(x => {
            this.comments.get(x).remove();
            this.comments = this.comments.remove(x);
        });

        this.items = items;
    }

    commentUserDisplayName(fieldContext, value) {
        this.getElement(fieldContext).children().eq(0).text(value);
    }

    commentUserId(fieldContext, value) {
        // no display role
    }

    commentDate(fieldContext, value) {
        this.getElement(fieldContext).children().eq(1).text(moment.utc(value).local().format(this.resources.dateTimeFormat()));
    }

    commentText(fieldContext, value) {
        this.getElement(fieldContext).children().eq(2).text(value);
        this.$display.scrollTop(this.$display[0].scrollHeight);
    }

    workItemId(fieldContext, value) {
        // no display role
    }

    private getElement(identifier: string) {
        if (!this.comments.has(identifier)) {
            this.comments = this.comments.set(identifier,
                $('<div />').addClass('commentbox-comment').appendTo(this.$display)
                    .append($('<div />').addClass('commentbox-comment-user'))
                    .append($('<div />').addClass('commentbox-comment-date'))
                    .append($('<div />').addClass('commentbox-comment-text'))
            );
        }

        return this.comments.get(identifier);
    }

    focus() { this.$input.focus(); }

    applyEnabledState(value: boolean) {
        (<HTMLInputElement>this.$input[0]).disabled = !value;
        (<HTMLInputElement>this.$add[0]).disabled = !value;
    }

    tabIndexElements() {
        return this.$input;
    }
}

registerBinding('commentbox', CommentBox);